<template>
  <div
    ref="root"
    class="relative max-w-full"
    :class="block ? 'block w-full' : 'inline-block'"
  >
    <button
      type="button"
      class="inline-flex items-center justify-between gap-2 h-9 px-3 text-xs transition bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-primary/40 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
      :class="[
        open ? 'border-primary ring-2 ring-primary/40' : '',
        block ? 'w-full' : '',
      ]"
      :style="{ minWidth, maxWidth }"
      :title="selectedLabel || placeholder"
      :aria-expanded="open"
      @click.stop="toggle"
    >
      <span class="min-w-0 truncate text-start">{{
        selectedLabel || placeholder
      }}</span>
      <i
        class="mdi mdi-chevron-down text-[14px] text-slate-400 shrink-0 transition-transform"
        :class="open ? 'rotate-180 text-primary' : ''"
      ></i>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        class="fixed z-[90] overflow-hidden bg-white border shadow-lg rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700"
        :style="menuStyle"
        @click.stop
      >
        <div
          v-if="showSearch"
          class="p-2 border-b border-slate-100 dark:border-slate-800"
        >
          <div class="relative">
            <input
              ref="searchInput"
              v-model="query"
              type="search"
              class="w-full h-8 text-xs bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15"
              :class="lang === 'ar' ? 'pr-8 pl-3' : 'pl-8 pr-3'"
              :placeholder="searchPlaceholder"
              @keydown.stop
            />
            <i
              class="absolute text-sm -translate-y-1/2 mdi mdi-magnify text-slate-400 pointer-events-none"
              :class="
                lang === 'ar'
                  ? 'right-2.5 left-auto top-1/2'
                  : 'left-2.5 right-auto top-1/2'
              "
            ></i>
          </div>
        </div>

        <ul class="py-1 max-h-64 overflow-y-auto custom-scrollbar" role="listbox">
          <li v-for="opt in filteredOptions" :key="String(opt.value)">
            <button
              type="button"
              role="option"
              class="flex items-start w-full gap-2 px-3 py-2.5 text-xs text-start transition leading-snug"
              :class="
                isSelected(opt.value)
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-secondary/10 hover:text-secondary'
              "
              :aria-selected="isSelected(opt.value)"
              @click="select(opt.value)"
            >
              <i
                v-if="isSelected(opt.value)"
                class="mdi mdi-check text-[14px] shrink-0 mt-0.5"
              ></i>
              <span class="min-w-0 whitespace-normal break-words">
                {{ opt.label }}
              </span>
            </button>
          </li>
        </ul>

        <p
          v-if="!filteredOptions.length"
          class="px-3 py-4 text-xs text-center text-slate-400 dark:text-slate-500"
        >
          {{ emptyLabel }}
        </p>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  options: { type: Array, required: true },
  placeholder: { type: String, default: "" },
  searchPlaceholder: { type: String, default: "Search..." },
  emptyLabel: { type: String, default: "No results" },
  minWidth: { type: String, default: "9rem" },
  maxWidth: { type: String, default: "14rem" },
  menuMinWidth: { type: String, default: "18rem" },
  menuMaxWidth: { type: String, default: "24rem" },
  align: { type: String, default: "end" },
  searchable: { type: Boolean, default: null },
  block: { type: Boolean, default: false },
  lang: { type: String, default: "ar" },
});

const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const query = ref("");
const root = ref(null);
const searchInput = ref(null);
const menuStyle = ref({});

const selectedLabel = computed(() => {
  const found = props.options.find(
    (o) => String(o.value) === String(props.modelValue),
  );
  return found?.label ?? "";
});

const showSearch = computed(() => {
  if (props.searchable != null) return props.searchable;
  return props.options.length > 5;
});

const filteredOptions = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return props.options;
  return props.options.filter((opt) =>
    String(opt.label || "")
      .toLowerCase()
      .includes(q),
  );
});

function isSelected(value) {
  return String(value) === String(props.modelValue);
}

function parseLength(value) {
  const n = parseFloat(String(value).replace("rem", ""));
  return Number.isFinite(n) ? n : 0;
}

function positionMenu() {
  const btn = root.value?.querySelector("button");
  if (!btn) return;

  const rect = btn.getBoundingClientRect();
  const edge = 8;
  const gap = 4;

  const rootFont =
    parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const minW = Math.max(
    rect.width,
    parseLength(props.menuMinWidth) * rootFont,
    240,
  );
  const maxW = Math.min(
    parseLength(props.menuMaxWidth) * rootFont,
    window.innerWidth - edge * 2,
  );
  const menuW = Math.min(maxW, Math.max(minW, 280));

  const searchH = showSearch.value ? 48 : 0;
  const itemH = 44;
  const listH = Math.min(filteredOptions.value.length * itemH + 8, 256);
  const estimatedH = searchH + listH + 8;

  let top = rect.bottom + gap;
  if (top + estimatedH > window.innerHeight - edge) {
    top = Math.max(edge, rect.top - estimatedH - gap);
  }

  let left;
  if (props.align === "start") {
    left = rect.left;
  } else {
    left = rect.right - menuW;
  }
  left = Math.min(Math.max(edge, left), window.innerWidth - menuW - edge);

  menuStyle.value = {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    width: `${Math.round(menuW)}px`,
  };
}

async function toggle() {
  if (open.value) {
    open.value = false;
    query.value = "";
    return;
  }
  open.value = true;
  query.value = "";
  await nextTick();
  positionMenu();
  if (showSearch.value) searchInput.value?.focus();
}

function select(value) {
  emit("update:modelValue", value);
  open.value = false;
  query.value = "";
}

function onDocClick(e) {
  if (root.value && !root.value.contains(e.target)) {
    open.value = false;
    query.value = "";
  }
}

function onKeydown(e) {
  if (e.key === "Escape") {
    open.value = false;
    query.value = "";
  }
}

function onReposition() {
  if (open.value) positionMenu();
}

watch(filteredOptions, () => {
  if (open.value) nextTick(() => positionMenu());
});

onMounted(() => {
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onReposition);
  window.addEventListener("scroll", onReposition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
  document.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onReposition);
  window.removeEventListener("scroll", onReposition, true);
});
</script>
